import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from "axios";

import { API_URL, LOGIN_PATH } from "./apiConfig";
import { tokenStorage } from "./auth/tokenStorage";

const UNAUTHORIZED = 401;

const PUBLIC_ENDPOINTS = ["/login", "/forgot-password"];

const isPublicEndpoint = (url?: string): boolean =>
  PUBLIC_ENDPOINTS.some((endpoint) => url?.endsWith(endpoint) ?? false);

const redirectToLogin = (): void => {
  if (typeof window === "undefined") {
    return;
  }

  if (window.location.pathname !== LOGIN_PATH) {
    window.location.replace(LOGIN_PATH);
  }
};

abstract class Service {
  protected api: AxiosInstance;

  protected constructor(public baseURL: string = API_URL) {
    this.api = axios.create({
      baseURL,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json; charset=utf-8",
      },
    });

    this.api.interceptors.request.use((config) => this.attachToken(config));

    this.api.interceptors.response.use(
      (response) => {
        tokenStorage.extend();

        return response;
      },
      (error: AxiosError) => this.handleError(error),
    );
  }

  private attachToken(
    config: InternalAxiosRequestConfig,
  ): InternalAxiosRequestConfig {
    const token = tokenStorage.read();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  }

  private handleError(error: AxiosError): Promise<never> {
    if (
      error.response?.status === UNAUTHORIZED &&
      !isPublicEndpoint(error.config?.url)
    ) {
      tokenStorage.clear();
      redirectToLogin();
    }

    return Promise.reject(error);
  }
}

export default Service;
